import React from 'react';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { colors, layout, radius } from '../design-system/tokens';

/**
 * 목록 화면 공통 상태 표시 — 로딩 / 오류(재시도) / 비어 있음.
 * 세 화면이 각자 다른 문구를 쓰지 않도록 한곳에 모았다.
 *
 * skeleton: 표 형태의 로딩을 원하면 { rows, cols } 를 넘긴다.
 *   운영툴은 스피너보다 스켈레톤이 덜 깜빡여 레이아웃 점프가 없다.
 */
const DataState = ({ loading, error, onRetry, empty, emptyText, skeleton, children }) => {
  if (loading) {
    if (skeleton) return <TableSkeleton rows={skeleton.rows} cols={skeleton.cols} />;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Skeleton variant="rounded" width={220} height={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: layout.cardPadding, textAlign: 'center', bgcolor: colors.surface }}>
        <Typography variant="body2" sx={{ color: colors.status.danger.fg, mb: 2 }}>
          {error}
        </Typography>
        {onRetry && (
          <Button variant="contained" onClick={onRetry}>
            다시 시도
          </Button>
        )}
      </Paper>
    );
  }

  if (empty) {
    return (
      <Paper sx={{ p: layout.cardPadding + 1, textAlign: 'center', bgcolor: colors.surface }}>
        <Typography variant="body2" sx={{ color: colors.text.muted }}>
          {emptyText}
        </Typography>
      </Paper>
    );
  }

  return children;
};

/** 표 스켈레톤 — 실제 표와 같은 열 수로 그려 로딩→로드 시 점프를 없앤다 */
export const TableSkeleton = ({ rows = 6, cols = 6 }) => (
  <Paper sx={{ p: 0, overflow: 'hidden', borderRadius: `${radius.md}px` }}>
    <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Table size="small" sx={{ minWidth: Math.max(cols * 120, 520) }}>
        <TableBody>
          {Array.from({ length: rows }).map((_, r) => (
            <TableRow key={r}>
              {Array.from({ length: cols }).map((__, c) => (
                <TableCell key={c}>
                  <Skeleton variant="text" width={c === 0 ? '70%' : '55%'} height={22} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  </Paper>
);

export default DataState;
